import {
  type HubDeletedWorkflow,
  type HubStartWorkflow,
  type HubUpdateWorkflowMetadata,
  type HubWorkflow,
  HubWorkflowStatus,
} from '@robovision/hub'
import {
  addOrReplaceByKey,
  distinct,
  HttpStatusCode,
  mergeArraysByKey,
} from '@robovision/rvai-utils'
import { defineStore } from 'pinia'
import {
  reactive,
  toRefs,
} from 'vue'
import { ApiErrorId } from '~domain/ApiErrorId'
import { Workflow } from '~domain/tests/Workflow'
import { ability } from '~services/ability'
import { hubApi } from '~services/api'
import { acceptStatus } from '~utils/api'


export const useWorkflowsStore = defineStore('workflows', () => {
  const state = reactive({
    datasetWorkflows: new Map<string, string[]>(),
    projectsWorkflows: new Map<string, string[]>(),
    recentlyCompletedWorkflows: {} as Record<string, string[]>,
    workflows: [] as Workflow[],
  })

  const getters = {
    getDatasetWorkflows (datasetId: string): Workflow[] {
      const ids = state.datasetWorkflows.get(datasetId) ?? []

      return ids
        .map(id => state.workflows.find(w => w.id === id))
        .filter(Boolean)
    },

    getProjectWorkflows (projectId: string, tags: string[] = []): Workflow[] {
      const ids = state.projectsWorkflows.get(projectId) ?? []

      return ids
        .map(id => state.workflows.find(w => w.id === id))
        .filter(Boolean)
        .filter(w => tags.some(t => w.tags.includes(t)))
    },

    getRecentlyCompletedWorkflowsCount (projectId: string, tags?: string[]): number {
      const ids = state.recentlyCompletedWorkflows[projectId] ?? []
      const workflows = getters.getProjectWorkflows(projectId, tags)

      return workflows.filter(v => ids.includes(v.id)).length
    },

    getWorkflow (id: string): Workflow | undefined {
      return state.workflows.find(w => w.id === id)
    },
  }

  function updateWorkflowState (updated: Workflow): void {
    const lastWorkflow = state.workflows.find(w => w.id === updated.id)
    if (lastWorkflow?.isInProgress && updated.isFinished) {
      state.recentlyCompletedWorkflows[updated.projectId] = [
        ...state.recentlyCompletedWorkflows[updated.projectId] ?? [],
        updated.id,
      ]
    }

    state.projectsWorkflows.set(
      updated.projectId,
      distinct([updated.id, ...state.projectsWorkflows.get(updated.projectId) ?? []]),
    )

    state.workflows = addOrReplaceByKey(
      state.workflows,
      updated,
      'id',
    )
  }

  const actions = {
    async checkWorkflowExists (workflowId: string): Promise<Workflow | undefined> {
      const response = await hubApi.workflows.getWorkflow(
        workflowId,
        { validateStatus: acceptStatus(HttpStatusCode.NOT_FOUND) },
      )
      if (!response?.data || response.data.id === ApiErrorId.WorkflowNotFound) return undefined

      return Workflow.fromResponse(response.data)
    },

    async deleteWorkflow (workflowId: string): Promise<void> {
      await hubApi.workflows.deleteWorkflow(workflowId)

      state.workflows = state.workflows.filter(v => v.id !== workflowId)
      const entry = [...state.projectsWorkflows.entries()]
        .find(([, workflowIds]) => workflowIds.includes(workflowId))
      if (!entry) return

      const [projectId, workflows] = entry
      state.projectsWorkflows.set(projectId, workflows.filter(id => id !== workflowId) ?? [])
    },

    handleWorkflowDeletedEvent (deletedWorkflow: HubDeletedWorkflow) {
      state.workflows = state.workflows.filter(v => v.id !== deletedWorkflow.id)
      const entry = [...state.projectsWorkflows.entries()]
        .find(([, workflowIds]) => workflowIds.includes(deletedWorkflow.id))
      if (!entry) return

      const [projectId, workflows] = entry
      state.projectsWorkflows.set(projectId, workflows.filter(id => id !== deletedWorkflow.id))
    },

    handleWorkflowEvent (data: HubWorkflow): void {
      const workflow = Workflow.fromResponse(data)

      if (!workflow.projectId) return

      updateWorkflowState(workflow)
    },
    async loadDatasetWorkflows (datasetId: string, datasetDependencyIds: string[]): Promise<string[]> {
      if (ability.cannot('read', 'Model') || ability.cannot('read', 'Test')) return []

      const { data } = await hubApi.workflows.listWorkflows({
        datasetDependencyFilter: datasetDependencyIds.map(v => `${v}:${datasetId}`),
        // sort: ['-createdAt'],
        tags: ['test'],
      })
      const workflows = data.items
        .map(Workflow.fromResponse)
        .filter(w => w.status !== HubWorkflowStatus.DELETING)

      const workflowIds = workflows.map(w => w.id)
      state.datasetWorkflows.set(datasetId, workflowIds)
      state.workflows = mergeArraysByKey(state.workflows, workflows, 'id')

      return workflowIds
    },

    async loadProjectWorkflows (projectId: string, tags: string[] = []): Promise<string[]> {
      if (ability.cannot('read', 'Model') || ability.cannot('read', 'Test')) return []

      const { data } = await hubApi.workflows.listWorkflows({
        projectId,
        sort: ['-createdAt'],
        ...(tags.length && { tags }),
      })
      const workflows = data.items
        .map(Workflow.fromResponse)
        .filter(w => w.status !== HubWorkflowStatus.DELETING)

      const workflowIds = workflows.map(w => w.id)
      state.projectsWorkflows.set(projectId, workflowIds)
      state.workflows = mergeArraysByKey(state.workflows, workflows, 'id')

      return workflowIds
    },

    async loadWorkflows (filter: string[] = []): Promise<Workflow[]> {
      const { data } = await hubApi.workflows.listWorkflows({
        filter,
        sort: ['-createdAt'],
      })
      const workflows = data.items.map(Workflow.fromResponse)

      state.workflows = mergeArraysByKey(state.workflows, workflows, 'id')

      return workflows
    },

    resetRecentlyCompletedWorkflows (): void {
      state.recentlyCompletedWorkflows = {}
    },

    async startWorkflow (workflowData: HubStartWorkflow): Promise<Workflow> {
      const { data } = await hubApi.workflows.startWorkflow(workflowData)

      const workflow = Workflow.fromResponse(data)

      updateWorkflowState(workflow)

      return workflow
    },

    async stopWorkflow (workflowId: string): Promise<void> {
      const { data } = await hubApi.workflows.stopWorkflow(workflowId)
      const workflow = Workflow.fromResponse(data)

      if (workflow.projectId) {
        state.projectsWorkflows.set(workflow.projectId, [
          ...state.projectsWorkflows.get(workflow.projectId) ?? [],
          workflow.id,
        ])
      }
    },

    async updateWorkflowMetadata (projectId: string, workflowId: string, metadata: HubUpdateWorkflowMetadata) {
      const { data } = await hubApi.workflows.updateWorkflowMetadata(workflowId, metadata)
      const workflow = Workflow.fromResponse(data)

      state.projectsWorkflows.set(projectId, [
        ...state.projectsWorkflows.get(projectId) ?? [],
        workflow.id,
      ])

      const workflowIndex = state.workflows.findIndex(w => w.id === workflow.id)
      state.workflows.splice(workflowIndex, 1, workflow)

      return workflow.id
    },
  }

  return {
    ...toRefs(state),
    ...getters,
    ...actions,
  }
})
